// 「我的專案」清單（/studio、/preview）和專案的製作的「用戶」頁（/users）共用的外框：
//   上方列：牌子、（全站製作才有的）回管理頁、使用說明、設定、通知鈴鐺、自己、登出
//   專案的製作（帳號不是全站製作、在某個專案的角色是製作）：上方列下面多一條左邊選單「專案」「用戶」，樣子比照全站製作的管理側欄；
//     手機寬度時選單變成上方一條可以左右滑的分頁列。
//   別人：沒有選單，版面跟以前一樣。
// 誰算專案的製作、選單有哪幾項：lib/managedProjects.ts（純判斷，有測試）。

import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Folder, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from './auth';
import { helpUrl } from './helpLinks';
import { projectListPath } from './landing';
import { managerNavItems, type ManagerNavKey } from './managedProjects';
import AccountMenu from '../components/AccountMenu';
import Icon from '../components/Icon';
import NotificationBell from '../components/NotificationBell';
import './myProjects.css';

const NAV_ICON: Record<ManagerNavKey, LucideIcon> = {
  projects: Folder,
  users: Users,
};

interface Props {
  /** 是專案的製作 → 顯示左邊選單（沒有的話整個外框跟以前一樣） */
  manager: boolean;
  /** 現在是哪一頁（選單上標出來） */
  active: ManagerNavKey;
  children: ReactNode;
}

export default function MyProjectsShell({ manager, active, children }: Props) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  if (!user) return null;  // 外層 RequireAuth 已經擋掉沒登入的

  const top = (
    <header className="my-projects__top">
      <span className="my-projects__brand">
        <span className="my-projects__brand-mark" aria-hidden="true" />
        STAGE <strong>PREVIZ</strong>
      </span>
      <span className="my-projects__grow" />
      {user.role === 'admin' && <Link className="my-projects__link" to="/admin">回管理頁</Link>}
      <a className="my-projects__link" href={helpUrl()} target="_blank" rel="noopener">使用說明</a>
      <Link className="my-projects__link" to="/settings">設定</Link>
      {/* 站內通知（被指派、新版、留言…）：專案的製作會先落在這一頁，所以這裡也要有 */}
      <NotificationBell />
      <span className="my-projects__user">
        <AccountMenu nameClassName="my-projects__user-name" />
      </span>
      <button
        className="my-projects__link my-projects__logout"
        onClick={async () => { await logout(); navigate('/login'); }}
      >登出</button>
    </header>
  );

  if (!manager) {
    return (
      <div className="my-projects">
        {top}
        <main className="my-projects__main">{children}</main>
      </div>
    );
  }

  return (
    <div className="my-projects my-projects--with-nav">
      {top}
      <div className="my-projects__body">
        <aside className="my-projects__side">
          <nav className="my-projects__nav" aria-label="選單">
            {managerNavItems(projectListPath(user.role)).map(item => (
              <Link
                key={item.key}
                to={item.path}
                className={'my-projects__nav-link' + (item.key === active ? ' is-active' : '')}
                aria-current={item.key === active ? 'page' : undefined}
              >
                <span className="my-projects__nav-icon"><Icon icon={NAV_ICON[item.key]} size="md" /></span>
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </aside>
        <main className="my-projects__main">{children}</main>
      </div>
    </div>
  );
}
